import React, { useState } from 'react';

const EFFECTS   = ['Balanced','Comforting','Elevating','Energizing','Indulgent','Invigorating','Refreshing','Social','Versatile'];
const STRAINS   = ['indica', 'sativa', 'hybrid'];
const TERPENES  = ['Caryophyllene','Humulene','Limonene','Linalool','Myrcene','Pinene','Terpinolene'];

const inputCls  = 'w-full px-3 py-2 rounded-lg bg-white dark:bg-zinc-800 border border-sage-900/30 dark:border-zinc-700 text-sage-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 text-sm focus:outline-none focus:ring-2 focus:ring-sage-900/30 dark:focus:ring-zinc-600';
const labelCls  = 'block text-xs font-semibold text-sage-900/50 dark:text-zinc-400 mb-1 uppercase tracking-wide';

export default function RecipeEditor({ onSave, initialData }) {
  const isEditing = !!initialData;

  const [form, setForm] = useState({
    recipeName:            initialData?.recipeName              || '',
    effect:                initialData?.effect                  || '',
    strain:                initialData?.strain                  || '',
    terpene:               initialData?.terpene                 || '',
    tags:                  (initialData?.tags || []).join(', '),
    estimatedMgPerServing: initialData?.estimatedMgPerServing   ?? '',
    servings:              initialData?.servings                ?? '',
    chefNotes:             initialData?.chefNotes               || '',
    infusionMethod:        initialData?.infusionMethod          || '',
    ingredients:           initialData?.ingredients?.length ? [...initialData.ingredients] : [''],
    instructions:          initialData?.instructions?.length ? [...initialData.instructions] : [''],
  });

  const [errors, setErrors] = useState({});

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const setListItem = (field, idx, value) =>
    setForm(f => { const a = [...f[field]]; a[idx] = value; return { ...f, [field]: a }; });

  const addListItem    = (field) => setForm(f => ({ ...f, [field]: [...f[field], ''] }));
  const removeListItem = (field, idx) =>
    setForm(f => ({ ...f, [field]: f[field].filter((_, i) => i !== idx) }));

  const validate = () => {
    const e = {};
    if (!form.recipeName.trim())     e.recipeName = 'Recipe name is required.';
    if (!form.effect)                e.effect     = 'Select an effect.';
    if (!form.strain)                e.strain     = 'Select a strain.';
    if (!form.estimatedMgPerServing || isNaN(Number(form.estimatedMgPerServing)))
                                     e.mg         = 'Enter a valid mg per serving.';
    if (!form.servings || isNaN(Number(form.servings)))
                                     e.servings   = 'Enter a valid serving count.';
    if (!form.ingredients.some(i => i.trim()))
                                     e.ingredients   = 'Add at least one ingredient.';
    if (!form.instructions.some(i => i.trim()))
                                     e.instructions  = 'Add at least one step.';
    return e;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});

    onSave({
      ...(initialData || {}),
      recipeName:            form.recipeName.trim(),
      effect:                form.effect,
      strain:                form.strain,
      terpene:               form.terpene,
      tags:                  form.tags.split(',').map(t => t.trim()).filter(Boolean),
      estimatedMgPerServing: Number(form.estimatedMgPerServing),
      servings:              Number(form.servings),
      chefNotes:             form.chefNotes.trim(),
      infusionMethod:        form.infusionMethod.trim(),
      ingredients:           form.ingredients.filter(i => i.trim()),
      instructions:          form.instructions.filter(i => i.trim()),
      isCustom:              true,
    });
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5 text-sage-900 dark:text-zinc-100">

      {/* Recipe Name */}
      <div>
        <label htmlFor="re-recipeName" className={labelCls}>Recipe Name *</label>
        <input
          id="re-recipeName"
          value={form.recipeName} onChange={set('recipeName')}
          placeholder="e.g. Brown Butter Lemon Cake"
          className={inputCls}
        />
        {errors.recipeName && <p className="text-xs text-red-500 mt-1">{errors.recipeName}</p>}
      </div>

      {/* Effect / Strain / Terpene */}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label htmlFor="re-effect" className={labelCls}>Effect *</label>
          <select id="re-effect" value={form.effect} onChange={set('effect')} className={inputCls}>
            <option value="">Select…</option>
            {EFFECTS.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
          {errors.effect && <p className="text-xs text-red-500 mt-1">{errors.effect}</p>}
        </div>
        <div>
          <label htmlFor="re-strain" className={labelCls}>Strain *</label>
          <select id="re-strain" value={form.strain} onChange={set('strain')} className={inputCls}>
            <option value="">Select…</option>
            {STRAINS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
          {errors.strain && <p className="text-xs text-red-500 mt-1">{errors.strain}</p>}
        </div>
        <div>
          <label htmlFor="re-terpene" className={labelCls}>Terpene</label>
          <select id="re-terpene" value={form.terpene} onChange={set('terpene')} className={inputCls}>
            <option value="">None</option>
            {TERPENES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      {/* Mg + Servings */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="re-mg" className={labelCls}>Est. mg / serving *</label>
          <input
            id="re-mg"
            type="number" min="1" step="1"
            value={form.estimatedMgPerServing} onChange={set('estimatedMgPerServing')}
            placeholder="e.g. 10"
            className={inputCls}
          />
          {errors.mg && <p className="text-xs text-red-500 mt-1">{errors.mg}</p>}
        </div>
        <div>
          <label htmlFor="re-servings" className={labelCls}>Servings *</label>
          <input
            id="re-servings"
            type="number" min="1" step="1"
            value={form.servings} onChange={set('servings')}
            placeholder="e.g. 12"
            className={inputCls}
          />
          {errors.servings && <p className="text-xs text-red-500 mt-1">{errors.servings}</p>}
        </div>
      </div>

      {/* Tags */}
      <div>
        <label htmlFor="re-tags" className={labelCls}>
          Tags <span className="normal-case font-normal">(comma-separated)</span>
        </label>
        <input
          id="re-tags"
          value={form.tags} onChange={set('tags')}
          placeholder="e.g. breakfast, sweet, quick"
          className={inputCls}
        />
      </div>

      {/* Chef Notes */}
      <div>
        <label htmlFor="re-chefNotes" className={labelCls}>Chef Notes</label>
        <textarea
          id="re-chefNotes"
          value={form.chefNotes} onChange={set('chefNotes')}
          placeholder="A short vibe description or key tip for the cook…"
          rows={2} className={inputCls + ' resize-none'}
        />
      </div>

      {/* Infusion Method */}
      <div>
        <label htmlFor="re-infusionMethod" className={labelCls}>Infusion Method</label>
        <textarea
          id="re-infusionMethod"
          value={form.infusionMethod} onChange={set('infusionMethod')}
          placeholder="Describe how to make the infused oil or butter used in this recipe…"
          rows={3} className={inputCls + ' resize-none'}
        />
      </div>

      {/* Ingredients — dynamic list; each input gets aria-label */}
      <div>
        <p className={labelCls}>
          Ingredients * <span className="normal-case font-normal">(one per line)</span>
        </p>
        <div className="space-y-2">
          {form.ingredients.map((item, idx) => (
            <div key={idx} className="flex gap-2 items-center">
              <input
                aria-label={`Ingredient ${idx + 1}`}
                value={item}
                onChange={e => setListItem('ingredients', idx, e.target.value)}
                placeholder={idx === 0 ? 'e.g. 2 Tbsp cannabutter' : 'Next ingredient…'}
                className={inputCls + ' flex-1'}
              />
              {form.ingredients.length > 1 && (
                <button
                  type="button"
                  aria-label={`Remove ingredient ${idx + 1}`}
                  onClick={() => removeListItem('ingredients', idx)}
                  className="text-red-400 hover:text-red-600 font-bold text-xl leading-none"
                >×</button>
              )}
            </div>
          ))}
        </div>
        <button type="button" onClick={() => addListItem('ingredients')}
          className="mt-2 text-xs font-bold text-sage-900/50 dark:text-zinc-500 hover:text-sage-900 dark:hover:text-zinc-200 underline underline-offset-2 transition">
          + Add ingredient
        </button>
        {errors.ingredients && <p className="text-xs text-red-500 mt-1">{errors.ingredients}</p>}
      </div>

      {/* Instructions — dynamic list; each textarea gets aria-label */}
      <div>
        <p className={labelCls}>
          Steps * <span className="normal-case font-normal">(one per field)</span>
        </p>
        <div className="space-y-2">
          {form.instructions.map((step, idx) => (
            <div key={idx} className="flex gap-2 items-start">
              <span className="text-xs font-bold text-sage-900/30 dark:text-zinc-600 mt-2.5 w-5 shrink-0 text-right">
                {idx + 1}.
              </span>
              <textarea
                aria-label={`Step ${idx + 1}`}
                value={step}
                onChange={e => setListItem('instructions', idx, e.target.value)}
                placeholder={`Step ${idx + 1}…`}
                rows={2} className={inputCls + ' flex-1 resize-none'}
              />
              {form.instructions.length > 1 && (
                <button
                  type="button"
                  aria-label={`Remove step ${idx + 1}`}
                  onClick={() => removeListItem('instructions', idx)}
                  className="text-red-400 hover:text-red-600 font-bold text-xl leading-none mt-1.5"
                >×</button>
              )}
            </div>
          ))}
        </div>
        <button type="button" onClick={() => addListItem('instructions')}
          className="mt-2 text-xs font-bold text-sage-900/50 dark:text-zinc-500 hover:text-sage-900 dark:hover:text-zinc-200 underline underline-offset-2 transition">
          + Add step
        </button>
        {errors.instructions && <p className="text-xs text-red-500 mt-1">{errors.instructions}</p>}
      </div>

      <button
        type="submit"
        className="w-full font-bold py-3 rounded-full text-white text-sm transition hover:opacity-90"
        style={{ backgroundColor: 'var(--accent-bg)' }}
      >
        {isEditing ? 'Save Changes' : 'Create Recipe'}
      </button>
    </form>
  );
}
